import { styled } from "@mui/material/styles";
import {
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  tableCellClasses,
  TableContainer,
  TableHead,
  TableRow,
} from "@mui/material";
import { FONT_FAMILY } from "@/utils/font-family";
import { ITableProps } from "@/components/tables/interface";
import { useStyle } from "@/components/tables/style";
import { useGomakeTheme } from "@/hooks/use-gomake-thme";
import { useTheme } from "@/contexts/theme";
import { useTranslation } from "react-i18next";
import { PrimaryCheckbox } from "../check-box/primary-check-box";
import { CSSProperties, useMemo, useState } from "react";

const toLogicalTextAlign = (
  align?: "left" | "center" | "right"
): CSSProperties["textAlign"] => {
  if (align === "left") return "start";
  if (align === "right") return "end";
  return align;
};
import { ArrowUpward, ArrowDownward } from "@mui/icons-material";
import { adaptMarginRight } from "@/utils/adapter";
import { useIsMobile } from "@/hooks/use-responsive";
import {
  TableEmptyState,
  DEFAULT_EMPTY_STATE,
  IEmptyStateConfig,
} from "@/components/tables/table-empty-state";

const PrimaryTableCell = styled(TableCell)(() => {
  const { primaryColor } = useGomakeTheme();
  const isMobile = useIsMobile();

  return {
    borderInlineEnd: "var(--theme-table-column-divider, none)",
    [`&.${tableCellClasses.head}`]: {
      backgroundColor:
        "var(--theme-table-head-bg, var(--theme-primary-100, " +
        primaryColor(50) +
        "))",
      color:
        "var(--theme-table-head-color, var(--theme-text-primary, " +
        primaryColor(900) +
        "))",
      ...FONT_FAMILY.Lexend(500, 14),
      padding:
        "var(--theme-table-cell-padding, " +
        (isMobile ? "5px 10px" : "5px") +
        ")",
      height: "var(--theme-table-row-height, 47px)",
      minHeight: "var(--theme-table-row-height, 47px)",
      fontSize: "var(--theme-table-head-font-size, 14px)",
      textAlign: "var(--theme-table-header-align, center)",
      borderBottom: "var(--theme-table-row-divider, 1px solid #E8E8E8)",
    },
    [`&.${tableCellClasses.body}`]: {
      ...FONT_FAMILY.Lexend(500, 14),
      color:
        "var(--theme-table-body-color, var(--theme-text-primary, " +
        primaryColor(500) +
        "))",
      padding:
        "var(--theme-table-cell-padding, " +
        (isMobile ? "5px 10px" : "3px 0") +
        ")",
      height: "var(--theme-table-row-height, 47px)",
      minHeight: "var(--theme-table-row-height, 47px)",
      fontSize: "var(--theme-table-cell-font-size, 14px)",
      textAlign: "var(--theme-table-cell-align, center)",
    },
  };
});

const PrimaryTableRow = styled(TableRow)(() => ({
  "&:nth-of-type(even)": {
    backgroundColor: "var(--theme-table-row-even-bg, transparent)",
  },
  "&:nth-of-type(odd)": {
    backgroundColor: "var(--theme-table-row-odd-bg, transparent)",
  },
  position: "relative",
  "&::after": {
    content: '""',
    position: "absolute",
    left: 0,
    right: 0,
    bottom: 0,
    height: "1px",
    background: "var(--theme-table-row-divider, transparent)",
  },
  "&:last-child td, &:last-child th": {
    borderBottom: "none",
  },
}));

const TableHeaderRow = styled(TableRow)(() => ({
  position: "relative",
  "&::after": {
    content: '""',
    position: "absolute",
    left: 0,
    right: 0,
    bottom: 0,
    height: "1px",
    background: "var(--theme-table-row-divider, transparent)",
  },
}));

const OddEvenTableRow = styled(TableRow)(() => ({
  "&:nth-of-type(even)": {
    backgroundColor: "var(--theme-table-row-even-bg, #ececec) !important",
  },
  "&:nth-of-type(odd)": {
    backgroundColor: "var(--theme-table-row-odd-bg, transparent)",
  },
  position: "relative",
  "&::after": {
    content: '""',
    position: "absolute",
    left: 0,
    right: 0,
    bottom: 0,
    height: "1px",
    background: "var(--theme-table-row-divider, transparent)",
  },
  "&:last-child td, &:last-child th": {
    borderBottom: "none",
  },
}));

const ClassicTableCell = styled(TableCell)(() => ({
  borderInlineEnd: "var(--theme-table-column-divider, none)",
  [`&.${tableCellClasses.head}`]: {
    color: "var(--theme-table-classic-head-color, #B5B7C0)",
    ...FONT_FAMILY.Lexend(500, 14),
    padding: "var(--theme-table-cell-padding, 5px 5px)",
    height: "var(--theme-table-row-height, 47px)",
    minHeight: "var(--theme-table-row-height, 47px)",
    textAlign: "var(--theme-table-header-align, center)",
    borderBottom: "var(--theme-table-row-divider, 1px solid #E8E8E8)",
  },
  [`&.${tableCellClasses.body}`]: {
    ...FONT_FAMILY.Lexend(500, 14),
    color: "var(--theme-table-body-color, var(--theme-text-primary, #292D32))",
    padding: "var(--theme-table-cell-padding, 3px 0)",
    height: "var(--theme-table-row-height, 47px)",
    minHeight: "var(--theme-table-row-height, 47px)",
    textAlign: "var(--theme-table-cell-align, center)",
  },
}));

const ClassicTableRow = styled(TableRow)(() => ({
  position: "relative",
  "&::after": {
    content: '""',
    position: "absolute",
    left: 0,
    right: 0,
    bottom: 0,
    height: "1px",
    background: "var(--theme-table-row-divider, transparent)",
  },
  "&:last-child td, &:last-child th": {
    borderBottom: "none",
  },
  "&:first-child td, &:first-child th": {
    borderBottom:
      "1px solid var(--theme-table-border-color, var(--theme-border-color, #e0e0e0))",
  },
}));

const RoundTableCell = styled(TableCell)(() => ({
  borderInlineEnd: "var(--theme-table-column-divider, none)",
  [`&.${tableCellClasses.head}`]: {
    ...FONT_FAMILY.Lexend(500, 13),
    backgroundColor:
      "var(--theme-table-round-head-bg, var(--theme-bg-secondary, #F9FAFB))",
    borderBottom:
      "var(--theme-table-row-divider, 1px solid var(--theme-table-border-color, var(--theme-border-color, #EAECF0))) !important",
    color:
      "var(--theme-table-round-head-color, var(--theme-text-secondary, #475467))",
    padding: "var(--theme-table-cell-padding, 5px 5px)",
    height: "var(--theme-table-row-height, 40px) !important",
    minHeight: "var(--theme-table-row-height, 40px)",
    textAlign: "var(--theme-table-header-align, center)",
  },
  [`&.${tableCellClasses.body}`]: {
    color: "var(--theme-table-body-color, var(--theme-text-primary, #292D32))",
    padding: "var(--theme-table-cell-padding, 5px 5px)",
    height: "var(--theme-table-row-height, 47px)",
    minHeight: "var(--theme-table-row-height, 47px)",
    ...FONT_FAMILY.Lexend(400, 14),
    textAlign: "var(--theme-table-cell-align, center)",
  },
}));

const RoundTableRow = styled(TableRow)(() => ({
  "&:nth-of-type(even)": {
    backgroundColor: "var(--theme-table-row-even-bg, transparent)",
  },
  "&:nth-of-type(odd)": {
    backgroundColor: "var(--theme-table-row-odd-bg, transparent)",
  },
  position: "relative",
  "&::after": {
    content: '""',
    position: "absolute",
    left: 0,
    right: 0,
    bottom: 0,
    height: "1px",
    background: "var(--theme-table-row-divider, transparent)",
  },
  "&:last-child td, &:last-child th": {
    borderBottom: "none",
  },
}));

const StyledTableContainer = styled(TableContainer)(() => ({
  // Enable horizontal scrolling
  overflowX: "auto",

  "&::-webkit-scrollbar": {
    width: "10px",
    height: "10px", // For horizontal scrollbar height
  },
  "&::-webkit-scrollbar-track": {
    background:
      "var(--theme-table-scrollbar-track, var(--theme-bg-tertiary, #f1f1f1))",
  },
  "&::-webkit-scrollbar-thumb": {
    background: "var(--theme-table-scrollbar-thumb, #CCCDCF)",
    borderRadius: "10px",
  },
  "&::-webkit-scrollbar-thumb:hover": {
    cursor: "pointer",
  },
}));

const PrimaryTable = ({
  rows,
  headers,
  stickyHeader,
  stickyFirstCol,
  maxHeight,
  variant,
  withoutShadow,
  columnWidths,
  columnAlignments,
  dataTour,
  isLastItemTotal = false,
  isAgingeport = false,
  withSort = false,
  enableCheckboxes = false,
  isAgingDetailedReport = false,
  withSortBE = false,
  onClickSortBE,
  onSelectionChange,
  rowCheckboxCondition = () => true,
  handleSortAgingReport,
  isDescOrder,
  agingReportOrderBy,
  isDescOrderBE = false,
  isHeaderSortable,
  docType,
  customSortFns, // Optional. Provide column-specific sort functions (by column index). Defaults to basic comparison if not passed.
  sortConfig: externalSortConfig,
  onSortChange,
  defaultSortIndex,
  defaultSortDirection,
  rowMetadata, // Optional. Array of booleans indicating if each row is a sub-product
  emptyState,
  hideEmptyState,
  fullWidthFooterRows,
  fullWidthRows,
}: ITableProps & { rowMetadata?: boolean[] }) => {
  const { t } = useTranslation();
  const dir: "rtl" | "ltr" = t("direction");
  const { currentTheme } = useTheme();
  const { classes } = useStyle(maxHeight, dir);
  const { primaryColor } = useGomakeTheme();
  const isHp = currentTheme === "hp-client";
  const [selectedRows, setSelectedRows] = useState<Set<number>>(new Set());
  const [hoveredColumn, setHoveredColumn] = useState<string | null>(null);

  // State for sorting (uncontrolled mode)
  const [internalSortConfig, setInternalSortConfig] = useState<{
    key: number;
    direction: "asc" | "desc";
  } | null>(
    withSort && defaultSortIndex !== undefined
      ? {
        key: defaultSortIndex as number,
        direction: defaultSortDirection || "desc",
      }
      : null,
  );
  const sortConfig = externalSortConfig ?? internalSortConfig;
  const handleRowSelection = (rowIndex: number) => {
    setSelectedRows((prevSelectedRows) => {
      const newSelectedRows = new Set(prevSelectedRows);
      if (newSelectedRows.has(rowIndex)) {
        newSelectedRows.delete(rowIndex);
      } else {
        newSelectedRows.add(rowIndex);
      }
      // Trigger the onSelectionChange callback
      onSelectionChange?.(newSelectedRows);
      return newSelectedRows;
    });
  };

  const handleSelectAll = () => {
    if (selectedRows?.size === rows?.filter(rowCheckboxCondition).length) {
      setSelectedRows(new Set());
      onSelectionChange?.(new Set());
    } else {
      const allSelected = new Set<number>();
      rows.forEach((_, index) => {
        if (rowCheckboxCondition(rows[index])) allSelected.add(index);
      });
      setSelectedRows(allSelected);
      onSelectionChange?.(allSelected);
    }
  };

  const TableCell =
    variant === "ClassicTable"
      ? ClassicTableCell
      : variant === "RoundTable"
        ? RoundTableCell
        : PrimaryTableCell;

  // HP theme: always use odd/even row styling (revert by removing "|| isHp")
  const TableRow =
    variant === "ClassicTable"
      ? ClassicTableRow
      : variant === "OddEvenTable" || isHp
        ? OddEvenTableRow
        : variant === "RoundTable"
          ? RoundTableRow
          : PrimaryTableRow;

  // Sorting logic
  const sortedRows = useMemo(() => {
    if (!sortConfig) return rows;

    const { key, direction } = sortConfig;
    const sortFn = customSortFns?.[key];

    return [...rows].sort((a, b) => {
      const result = sortFn
        ? sortFn(a, b)
        : a[key] < b[key]
          ? -1
          : a[key] > b[key]
            ? 1
            : 0;
      return direction === "asc" ? result : -result;
    });
  }, [rows, sortConfig, customSortFns]);

  const handleSort = (index: number) => {
    const next = (() => {
      if (sortConfig?.key === index) {
        return {
          key: index,
          direction: sortConfig.direction === "asc" ? "desc" : ("asc" as const),
        };
      }
      return { key: index, direction: "asc" as const };
    })();

    if (onSortChange) {
      onSortChange(next as { key: number; direction: "asc" | "desc" });
    } else {
      setInternalSortConfig(next as { key: number; direction: "asc" | "desc" });
    }
  };

  const showEmptyState = (sortedRows?.length ?? 0) === 0 && !hideEmptyState;
  const resolvedEmptyState: IEmptyStateConfig = showEmptyState
    ? { ...DEFAULT_EMPTY_STATE, ...emptyState }
    : DEFAULT_EMPTY_STATE;
  const emptyColSpan = (headers?.length ?? 0) + (enableCheckboxes ? 1 : 0);

  return (
    <Paper
      elevation={0}
      sx={{
        width: "var(--theme-table-container-width, 100%)",
        overflow: "hidden",
        height: "100%",
        boxSizing: "border-box",
        background: "var(--theme-table-container-bg, #FFF)",
        border: "var(--theme-table-container-border, none)",
        borderRadius: "var(--theme-table-container-radius, 0)",
        boxShadow: withoutShadow
          ? "none"
          : "var(--theme-table-paper-shadow, 0px 2px 1px -1px rgba(0,0,0,0.2), 0px 1px 1px 0px rgba(0,0,0,0.14), 0px 1px 3px 0px rgba(0,0,0,0.12))",
        ...(variant === "RoundTable" && {
          borderRadius: "var(--theme-radius-lg, 12px)",
          border:
            "1px solid var(--theme-table-border-color, var(--theme-border-color, #EAECF0)) !important",
        }),
      }}
    >
      <StyledTableContainer
        dir={dir}
        style={{
          ...classes.tableContainer,
          width: "var(--theme-table-container-width, 100%)",
          boxShadow:
            "var(--theme-table-container-shadow, 0px 4px 40px 0px #00000014)",
          ...(showEmptyState && {
            minHeight: maxHeight ?? "calc(50vh - 200px)",
          }),
        }}
      >
        {showEmptyState && (
          <TableEmptyState
            config={resolvedEmptyState}
            wrapperStyle={{
              position: "absolute",
              left: 0,
              right: 0,
              top: 52,
              bottom: 0,
            }}
          />
        )}
        <Table
          data-tour={dataTour}
          stickyHeader={stickyHeader}
          dir={dir}
          sx={{ width: "var(--theme-table-width, 100%)" }}
        >
          <TableHead>
            <TableHeaderRow>
              {/* Checkbox column */}
              {enableCheckboxes && (
                <TableCell padding="checkbox">
                  <PrimaryCheckbox
                    checked={
                      selectedRows?.size ===
                      rows?.filter(rowCheckboxCondition).length
                    }
                    onChange={handleSelectAll}
                    inputProps={{ "aria-label": "select all" }}
                  />
                </TableCell>
              )}
              {/* Headers */}
              {headers?.map((header, index) => (
                <TableCell
                  key={`header_${index}`}
                  align={undefined}
                  onMouseEnter={() => setHoveredColumn(header)}
                  onMouseLeave={() => setHoveredColumn(null)}
                  style={{
                    cursor: withSort || withSortBE ? "pointer" : "default",
                    userSelect: "none",
                    ...(columnWidths?.[index]
                      ? { width: columnWidths[index] }
                      : {}),
                    ...(columnAlignments?.[index]
                      ? { textAlign: toLogicalTextAlign(columnAlignments[index]) }
                      : {}),
                    ...(index === 0 && stickyHeader
                      ? { position: "sticky", top: 0 }
                      : {}),
                  }}
                  onClick={() => {
                    if (withSort) handleSort(index);
                    if (withSortBE) onClickSortBE(header);
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      width: "100%",
                      justifyContent: columnAlignments?.[index]
                        ? columnAlignments[index] === "left"
                          ? "flex-start"
                          : columnAlignments[index] === "right"
                            ? "flex-end"
                            : "center"
                        : "var(--theme-table-header-justify, center)",
                      alignItems: "center",
                    }}
                  >
                    {isAgingeport ? header?.name : header}
                    {/* Show sorting icon: on hover OR when this header is sorted (for FE sort) */}
                    {withSort &&
                      !isAgingeport &&
                      header !== "" &&
                      (sortConfig?.key === index ||
                        hoveredColumn === header) && (
                        <IconButton
                          size="small"
                          style={{ ...adaptMarginRight(dir, 4) }}
                        >
                          {sortConfig?.key === index ? (
                            sortConfig.direction === "asc" ? (
                              <ArrowUpward sx={{ fontSize: "18px" }} />
                            ) : (
                              <ArrowDownward sx={{ fontSize: "18px" }} />
                            )
                          ) : (
                            <ArrowUpward sx={{ fontSize: "18px" }} />
                          )}
                        </IconButton>
                      )}
                    {isAgingeport &&
                      (header?.key === "cardName" ||
                        header?.key === "cardCode" ||
                        header?.key === "balanceDue") && (
                        <IconButton
                          size="small"
                          style={{ ...adaptMarginRight(dir, 4) }}
                          onClick={() => handleSortAgingReport(header?.key)}
                        >
                          {agingReportOrderBy === header?.key &&
                            !isDescOrder ? (
                            <ArrowUpward sx={{ fontSize: "18px" }} />
                          ) : agingReportOrderBy === header?.key &&
                            isDescOrder ? (
                            <ArrowDownward sx={{ fontSize: "18px" }} />
                          ) : (
                            <ArrowUpward
                              sx={{ fontSize: "18px", opacity: 0.35 }}
                            />
                          )}
                        </IconButton>
                      )}

                    {withSortBE && isHeaderSortable(docType, header) && (
                      <IconButton
                        size="small"
                        style={{
                          ...adaptMarginRight(dir, 4),
                          visibility:
                            hoveredColumn === header ? "visible" : "hidden",
                        }}
                      >
                        {!isDescOrderBE ? (
                          <ArrowUpward sx={{ fontSize: "18px" }} />
                        ) : (
                          <ArrowDownward sx={{ fontSize: "18px" }} />
                        )}
                      </IconButton>
                    )}
                  </div>
                </TableCell>
              ))}
            </TableHeaderRow>
          </TableHead>
          <TableBody>
            {(sortedRows?.length ?? 0) > 0 ? (
              sortedRows?.map((row, rowIndex) => {
                // Check if this row is a sub-product row
                const isSubProductRow = rowMetadata && rowMetadata[rowIndex];
                const isFullWidthRow = !!fullWidthRows?.[rowIndex];
                const colSpan = emptyColSpan;

                return (
                  <TableRow
                    key={`row_${rowIndex}`}
                    style={{
                      backgroundColor: isSubProductRow
                        ? primaryColor(50)
                        : isAgingeport && isAgingDetailedReport && row[0] == " "
                          ? "#F6F6F6"
                          : isLastItemTotal && rowIndex === rows.length - 1
                            ? "#EFDDFF"
                            : "inherit",
                    }}
                  >
                    {enableCheckboxes && !isFullWidthRow && (
                      <TableCell padding="checkbox">
                        {rowCheckboxCondition(row) ? (
                          <PrimaryCheckbox
                            checked={selectedRows.has(rowIndex)}
                            onChange={() => handleRowSelection(rowIndex)}
                            inputProps={{
                              "aria-label": `select row ${rowIndex}`,
                            }}
                          />
                        ) : null}
                      </TableCell>
                    )}
                    {isFullWidthRow ? (
                      <TableCell
                        colSpan={colSpan}
                        style={{
                          padding: 0,
                          borderBottom: "1px solid #E8E8E8",
                        }}
                      >
                        {row[0]}
                      </TableCell>
                    ) : (
                      row.map((cell, cellIndex) => {
                        const cellStyle = {
                          ...(columnWidths?.[cellIndex]
                            ? { width: columnWidths[cellIndex] }
                            : {}),
                          ...(columnAlignments?.[cellIndex]
                            ? { textAlign: columnAlignments[cellIndex] }
                            : {}),
                        };
                        if (cellIndex === 0 && stickyFirstCol) {
                          return (
                            <TableCell style={classes.sticky} key={cellIndex}>
                              {cell}
                            </TableCell>
                          );
                        }
                        return (
                          <TableCell style={cellStyle} key={cellIndex}>
                            {cell}
                          </TableCell>
                        );
                      })
                    )}
                  </TableRow>
                );
              })
            ) : !fullWidthFooterRows?.length ? (
              <TableRow>
                <TableCell
                  colSpan={emptyColSpan}
                  align="center"
                  style={{ padding: showEmptyState ? 0 : 20, border: "none" }}
                >
                  {showEmptyState ? null : null}
                </TableCell>
              </TableRow>
            ) : null}
            {fullWidthFooterRows?.map((content, index) => (
              <TableRow key={`footer-row-${index}`}>
                <TableCell
                  colSpan={emptyColSpan}
                  align="center"
                  style={{
                    padding: 0,
                    border: "none",
                    verticalAlign: "middle",
                  }}
                >
                  {content}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </StyledTableContainer>
    </Paper>
  );
};

export {
  TableCell,
  TableRow,
  PrimaryTable,
  PrimaryTableCell,
  PrimaryTableRow,
  RoundTableCell,
  RoundTableRow,
};
